import React, { Fragment, useState, useEffect } from "react";
import {
  Row,
  Col,
  CardHeader,
  CardBody,
  Button,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { RiDeleteBinLine } from "react-icons/ri";
import { FiPlusSquare } from "react-icons/fi";
import DatePicker from "react-datepicker";
import { showToast } from "../../Common/Toast";
import { showSweetAlert } from "../../Common/SweetAlert";
import { fetchJobseekerDataRequest } from "../../../redux/jobseeker/fetch_Jobseeker_data/actions";
import {
  addWorkSampleRequest,
  deleteWorkSampleRequest,
  resetData,
} from "../../../redux/jobseeker/work_sample/actions";
import AddSampleCard from "./AddSampleCard";

const Worksample = ({ profileData, validationMessages }) => {
  const dispatch = useDispatch();
  const { cand_id } = useParams();
  const [workSamples, setWorkSamples] = useState([]);
  const [errors, setErrors] = useState({});
  const [showAddSample, setShowAddSample] = useState(false);

  useEffect(() => {
    if (profileData.work_sample) {
      setWorkSamples(profileData.work_sample);
    }
  }, [profileData]);

  const handleChange = (e, field, index) => {
    const updatedSamples = workSamples.map((sample, idx) =>
      idx === index ? { ...sample, [field]: e.target.value } : sample
    );
    setWorkSamples(updatedSamples);

    // Clear the error for the specific field if it's being typed in
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (updatedErrors[index] && updatedErrors[index][field]) {
        delete updatedErrors[index][field];
      }
      return updatedErrors;
    });
  };

  const handleEndDateChange = (date, index) => {
    // Create a copy of the current errors for the specific index
    const updatedErrors = { ...errors };
    updatedErrors[index] = updatedErrors[index] || {}; // Ensure the index exists

    // Clear the error for the end date when it's changed
    updatedErrors[index].work_end = "";

    // Get the current work sample and its start date
    const currentSample = workSamples[index];
    const workStartDate = new Date(currentSample.work_start);


    const endDate = new Date(date);

    // Check if the start date is set and if the end date is before the start date
    if (workStartDate && endDate < workStartDate) {
      updatedErrors[index].work_end = "End date cannot be earlier than start date";
      setErrors(updatedErrors);
    } else {
      // Update the work sample with the new end date
      const updatedSamples = workSamples.map((sample, idx) =>
        idx === index ? { ...sample, work_end: date } : sample
      );

      // Update the state with the new samples and clear errors
      setWorkSamples(updatedSamples);
      setErrors(updatedErrors);
    }
  };

  const handleStartDateChange = (date, index) => {
    // Create a copy of the current errors for the specific index
    const updatedErrors = { ...errors };
    updatedErrors[index] = updatedErrors[index] || {}; // Ensure the index exists

    // Clear the error for the start date when it's changed
    updatedErrors[index].work_start = "";

    // Get the current date
    const currentDate = new Date();

    // Check if the selected date is greater than the current date
    if (date > currentDate) {
      updatedErrors[index].work_start = "Start date cannot be greater than the current date";
      setErrors(updatedErrors);
      return; // Exit the function early
    }

    // Update the work sample with the new start date
    const updatedSamples = workSamples.map((sample, idx) =>
      idx === index ? { ...sample, work_start: date } : sample
    );

    // Get the work end date for the current sample
    const workEndDate = new Date(updatedSamples[index].work_end);
    console.log("workEndDate4", date, workEndDate);

    // Check if the end date is set and if the start date is after the end date
    if (workEndDate && date > workEndDate) {
      updatedErrors[index].work_start = "Start date cannot be later than end date";
      setErrors(updatedErrors);
    } else {
      // If the start date is valid, update the state with the new samples and clear errors
      setWorkSamples(updatedSamples);
      setErrors(updatedErrors);
    }
  };

  const validateWorkSample = (sample) => {
    const errors = {};
    if (!sample.work_title?.trim())
      errors.work_title = "Work title is required.";
    if (!sample.work_start) {
      errors.work_start = "Start date is required.";
    } else {
      const workStart = new Date(sample.work_start); // ✅ use sample, not formData
      const dobDate = new Date(profileData.jobseeker_date_of_birth); // props.dob = "1993-07-28"

      if (workStart < dobDate) {
        errors.work_start = "Start date cannot be before Date of Birth.";
        // isValid = false;
      }
    }

    if (!sample.work_end) errors.work_end = "End date is required.";
    if (
      sample.work_start &&
      sample.work_end &&
      sample.work_start > sample.work_end
    ) {
      errors.work_end = "End date must be after start date.";
    }
    if (!sample.work_description) {
      errors.work_description = "Work description is required.";

    } else {

      const onlyNumbersWorkDescriptionRegex = /^\d+$/; // Only numbers

      if (onlyNumbersWorkDescriptionRegex.test(sample.work_description)) {
        errors.work_description = "Work description cannot contain only numbers.";

      }
      // Minimum length check (50 characters)
      if (sample.work_description.length < 15) {
        errors.work_description = "Work description must be at least 15 characters.";

      }
    }
    // Regular expression for URL validation
    const urlRegex = /^(https?:\/\/)([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(\/\S*)?$/;

    // Validate work URL
    if (sample.work_url && !urlRegex.test(sample.work_url)) {
      errors.work_url = "Please enter a valid URL.";
    }
    return errors;
  };

  const handleSaveWorkSample = (index) => {
    const entry = workSamples[index]; // Get the specific work sample entry by index
    const validationErrors = validateWorkSample(entry);

    if (Object.keys(validationErrors).length > 0) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [index]: validationErrors,
      }));
      return;
    }

    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors[index];
      return updatedErrors;
    });

    const formattedData = {
      work_sample_id: entry.id || "",
      work_title: entry.work_title || "",
      work_url: entry.work_url || "",
      work_start: entry.work_start
        ? new Date(entry.work_start).toISOString()
        : "",
      work_end: entry.work_end ? new Date(entry.work_end).toISOString() : "",
      work_description: entry.work_description || "",
    };

    // Dispatch the action with formatted data
    dispatch(addWorkSampleRequest(formattedData));
  };

  const handleDeleteWorkSampleClick = (job_id) => {
    showSweetAlert("confirm", {
      message: "You want to delete the work sample",
      fun: handleDeleteWorkSample,
      id: job_id,
    });
  };

  const handleDeleteWorkSample = (workSampleId) => {
    dispatch(deleteWorkSampleRequest(workSampleId));
  };

  const addWorkSampleReducerData = useSelector(
    (state) => state.workSampleReducer
  );

  useEffect(() => {
    if (
      addWorkSampleReducerData.status &&
      addWorkSampleReducerData.message !== ""
    ) {
      showToast("success", addWorkSampleReducerData.message);
      dispatch(fetchJobseekerDataRequest());
      dispatch(resetData());
    } else if (
      addWorkSampleReducerData.status === false &&
      addWorkSampleReducerData.message !== ""
    ) {
      showToast("error", addWorkSampleReducerData.message);
      dispatch(resetData());
    }
  }, [addWorkSampleReducerData]);

  const [showValidationMessages, setValidationMessages] = useState();
  useEffect(() => {
    // Check if validationMessages is an object with the errors
    if (validationMessages && typeof validationMessages === "object") {
      setValidationMessages(validationMessages);
      console.log("validationMessages888", validationMessages);
    }
  }, [validationMessages]);

  return (
    <Fragment>
      <CardHeader className="py-3">
        <div className="d-flex align-items-center justify-content-between">
          <h4 className="card-title mb-0" style={{ fontWeight: 550 }}>
            Work Sample
          </h4>
          {cand_id ? null : (
            <FiPlusSquare
              className="add-icon"
              size={22}
              title="Add"
              onClick={() => setShowAddSample(true)}
            />
          )}
        </div>
      </CardHeader>
      <CardBody>
        <div>
          {showValidationMessages && (
            <div className="text-danger mb-4">
              {showValidationMessages.work_sample}
            </div>
          )}
          {showAddSample && (
            <AddSampleCard dob={profileData.jobseeker_date_of_birth} onClose={() => setShowAddSample(false)} />
          )}
          {workSamples.length > 0 ? (
            workSamples.map((work, index) => (
              <div className="mb-4" key={work.id}>
                <form>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>
                          Work Title<span className="font-danger">*</span>
                        </Label>
                        <Input
                          type="text"
                          name="work_title"
                          className="form-control form-control-sm"
                          placeholder="Enter work title"
                          onChange={(e) => handleChange(e, "work_title", index)}
                          value={work.work_title}
                        />
                        {errors[index]?.work_title && (
                          <small className="text-danger">
                            {errors[index].work_title}
                          </small>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>
                          Start Date<span className="text-danger">*</span>
                        </Label>
                        <DatePicker
                          showYearDropdown
                          showMonthDropdown
                          className="form-control form-control-sm mb-0"
                          selected={
                            work.work_start ? new Date(work.work_start) : null
                          }
                          onChange={(date) =>
                            handleStartDateChange(date, index)
                          }
                          dateFormat="dd-MM-yyyy"
                          placeholderText="Enter start date"
                          dropdownMode="select"
                        />
                        {errors[index]?.work_start && (
                          <small className="text-danger">
                            {errors[index].work_start}
                          </small>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>
                          End Date<span className="text-danger">*</span>
                        </Label>
                        <DatePicker
                          showYearDropdown
                          showMonthDropdown
                          className="form-control form-control-sm mb-0"
                          selected={
                            work.work_end ? new Date(work.work_end) : null
                          }
                          onChange={(date) => handleEndDateChange(date, index)}
                          dateFormat="dd-MM-yyyy"
                          placeholderText="Enter end date"
                          dropdownMode="select"
                        />
                        {errors[index]?.work_end && (
                          <small className="text-danger">
                            {errors[index].work_end}
                          </small>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Work URL</Label>
                        <Input
                          type="text"
                          name="work_url"
                          className="form-control form-control-sm"
                          placeholder="Enter work URL"
                          onChange={(e) => handleChange(e, "work_url", index)}
                          value={work.work_url}
                          maxLength={100}
                        />
                        {errors[index]?.work_url && (
                          <small className="text-danger">
                            {errors[index].work_url}
                          </small>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <FormGroup>
                        <Label>
                          Description<span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="textarea"
                          rows="3"
                          name="work_description"
                          placeholder="Enter work description"
                          onChange={(e) =>
                            handleChange(e, "work_description", index)
                          }
                          value={work.work_description}
                          maxLength={250}
                        />
                        {errors[index]?.work_description && (
                          <small className="text-danger">
                            {errors[index].work_description}
                          </small>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <div className="text-end">
                    <Button
                      type="button"
                      size="sm"
                      color="primary"
                      onClick={() => handleSaveWorkSample(index)}
                    >
                      Update
                    </Button>
                    {cand_id ? null : (
                      <Button
                        size="sm"
                        color="danger"
                        type="button"
                        className="ms-2"
                        onClick={() =>
                          handleDeleteWorkSampleClick({ id: work.id })
                        }
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </form>
              </div>
            ))
          ) : (
            <div className="text-center">No work samples added yet.</div>
          )}
        </div>
      </CardBody>
    </Fragment>
  );
};

export default Worksample;
